import { useCallback, useEffect, useRef, useState, type ClipboardEvent, type DragEvent } from 'react';
import SectionTabs from '../../components/SectionTabs';
import { SUBMIT_TABS } from '../../lib/section-tabs';
import { api } from '../../lib/api';
import { LoadingBlock } from '../../components/Spinner';

type ChangeStatus = 'queued' | 'working' | 'review' | 'merged' | 'closed' | 'failed';

interface ChangeSummary {
  number: number;
  title: string;
  state: 'open' | 'closed';
  status: ChangeStatus;
  url: string;
  prUrl?: string;
  prNumber?: number;
  prState?: 'open' | 'closed' | 'merged';
  createdAt: string;
  updatedAt: string;
  commentCount: number;
  requestedBy?: string;
}

interface ChangeComment {
  id: number;
  author: string;
  authorType: 'user' | 'bot';
  body: string;
  createdAt: string;
  source: 'issue' | 'pr';
}

interface ChangeDetail extends ChangeSummary {
  body: string;
  comments: ChangeComment[];
}

interface AttachedImage {
  dataBase64: string;
  contentType: string;
  previewUrl: string;
  fileName: string;
}

const STATUS_META: Record<ChangeStatus, { label: string; cls: string }> = {
  queued: { label: 'Queued', cls: 'bg-secondary' },
  working: { label: 'Copilot working', cls: 'bg-info text-dark' },
  review: { label: 'Ready to deploy', cls: 'bg-primary' },
  merged: { label: 'Done', cls: 'bg-success' },
  closed: { label: 'Closed', cls: 'bg-dark' },
  failed: { label: 'Failed', cls: 'bg-danger' }
};

function statusBadge(s: ChangeStatus) {
  const meta = STATUS_META[s];
  return <span className={`badge ${meta.cls}`}>{meta.label}</span>;
}

function timeAgo(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

async function fileToBase64(file: File): Promise<string> {
  const buf = new Uint8Array(await file.arrayBuffer());
  let bin = '';
  const chunk = 0x8000;
  for (let i = 0; i < buf.length; i += chunk) {
    bin += String.fromCharCode(...buf.subarray(i, i + chunk));
  }
  return btoa(bin);
}

const ALLOWED_IMAGE_TYPES = new Set(['image/png', 'image/jpeg', 'image/webp', 'image/gif']);
const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

export default function AdminChangeRequestPage() {
  const [items, setItems] = useState<ChangeSummary[]>([]);
  const [loadingList, setLoadingList] = useState(true);
  const [selected, setSelected] = useState<number | null>(null);
  const [detail, setDetail] = useState<ChangeDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [error, setError] = useState('');

  const refreshList = useCallback(async () => {
    try {
      const res = await api.get<{ items: ChangeSummary[] }>('/api/mgmt/change-requests');
      setItems(res.items);
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load history.');
    } finally {
      setLoadingList(false);
    }
  }, []);

  const refreshDetail = useCallback(async (num: number) => {
    try {
      const d = await api.get<ChangeDetail>(`/api/mgmt/change-requests/${num}`);
      setDetail(d);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load request.');
    } finally {
      setLoadingDetail(false);
    }
  }, []);

  useEffect(() => {
    void refreshList();
    const t = setInterval(() => void refreshList(), 30_000);
    return () => clearInterval(t);
  }, [refreshList]);

  useEffect(() => {
    if (selected == null) {
      setDetail(null);
      return;
    }
    setLoadingDetail(true);
    void refreshDetail(selected);
    const t = setInterval(() => void refreshDetail(selected), 20_000);
    return () => clearInterval(t);
  }, [selected, refreshDetail]);

  const handleCreated = (issueNumber: number) => {
    void refreshList();
    setSelected(issueNumber);
  };

  return (
    <SectionTabs ariaLabel="Submit sections" items={SUBMIT_TABS}>
      <h1>Request a website change</h1>
      <p className="text-muted">
        Describe what you'd like changed. Copilot picks it up, opens a pull
        request, and you click <strong>Deploy</strong> when it's ready. You can
        keep filing more requests &mdash; they run in parallel.
      </p>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      <div className="row g-4">
        <div className="col-lg-5">
          <NewRequestForm onCreated={handleCreated} />

          <h2 className="h5 mt-4">History</h2>
          {loadingList && items.length === 0 ? (
            <p className="text-muted">Loading&hellip;</p>
          ) : items.length === 0 ? (
            <p className="text-muted">No change requests yet.</p>
          ) : (
            <ul className="list-group">
              {items.map((it) => (
                <li
                  key={it.number}
                  className={`list-group-item list-group-item-action ${selected === it.number ? 'active' : ''}`}
                >
                  <button
                    type="button"
                    className="btn btn-link text-start p-0 w-100 text-decoration-none"
                    onClick={() => setSelected(it.number)}
                    aria-current={selected === it.number}
                  >
                    <div className="d-flex justify-content-between align-items-start gap-2">
                      <span className={selected === it.number ? 'text-white fw-semibold' : 'fw-semibold'}>
                        #{it.number} {it.title}
                      </span>
                      {statusBadge(it.status)}
                    </div>
                    <div className={`small ${selected === it.number ? 'text-white-50' : 'text-muted'}`}>
                      Updated {timeAgo(it.updatedAt)} &middot; {it.commentCount}{' '}
                      {it.commentCount === 1 ? 'reply' : 'replies'}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="col-lg-7">
          {selected == null ? (
            <div className="card">
              <div className="card-body text-muted">
                Select a request on the left to view its conversation.
              </div>
            </div>
          ) : (
            <DetailPanel
              key={selected}
              detail={detail}
              loading={loadingDetail}
              onRefresh={() => void refreshDetail(selected)}
              onListRefresh={() => void refreshList()}
              onClose={() => setSelected(null)}
            />
          )}
        </div>
      </div>
    </SectionTabs>
  );
}

function ImageAttachment({ image, onRemove }: { image: AttachedImage; onRemove: () => void }) {
  return (
    <div className="d-flex align-items-center gap-2 mb-2 p-2 border rounded bg-light">
      <img src={image.previewUrl} alt="Attached preview" style={{ maxHeight: 80, maxWidth: 120 }} />
      <div className="flex-grow-1 small text-muted">{image.fileName}</div>
      <button type="button" className="btn btn-sm btn-outline-danger" onClick={onRemove} aria-label="Remove attached image">
        Remove
      </button>
    </div>
  );
}

function NewRequestForm({ onCreated }: { onCreated: (issueNumber: number) => void }) {
  const [description, setDescription] = useState('');
  const [image, setImage] = useState<AttachedImage | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [hover, setHover] = useState(false);
  const [err, setErr] = useState('');
  const fileInput = useRef<HTMLInputElement>(null);

  const attachFile = useCallback(async (file: File) => {
    setErr('');
    if (!ALLOWED_IMAGE_TYPES.has(file.type)) {
      setErr('Please attach a PNG, JPEG, WEBP, or GIF image.');
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      setErr('Image must be 10 MB or smaller.');
      return;
    }
    const dataBase64 = await fileToBase64(file);
    setImage({
      dataBase64,
      contentType: file.type,
      previewUrl: URL.createObjectURL(file),
      fileName: file.name || 'screenshot'
    });
  }, []);

  const onPaste = useCallback(
    async (e: ClipboardEvent<HTMLTextAreaElement>) => {
      const items = e.clipboardData?.items ?? [];
      for (const it of items) {
        if (it.kind === 'file') {
          const f = it.getAsFile();
          if (f) {
            e.preventDefault();
            await attachFile(f);
            return;
          }
        }
      }
    },
    [attachFile]
  );

  const onDrop = useCallback(
    async (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setHover(false);
      const f = e.dataTransfer?.files?.[0];
      if (f) await attachFile(f);
    },
    [attachFile]
  );

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!description.trim()) return;
    setSubmitting(true);
    setErr('');
    try {
      let screenshotUrl: string | undefined;
      if (image) {
        const upload = await api.post<{ url: string }>('/api/mgmt/change-requests/uploads', {
          dataBase64: image.dataBase64,
          contentType: image.contentType
        });
        screenshotUrl = upload.url;
      }
      const issue = await api.post<{ number: number; url: string }>('/api/mgmt/change-requests', {
        description: description.trim(),
        screenshotUrl
      });
      setDescription('');
      if (image) URL.revokeObjectURL(image.previewUrl);
      setImage(null);
      onCreated(issue.number);
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : 'Something went wrong.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} noValidate className="card mb-3">
      <div className="card-body">
        <h2 className="h5">New request</h2>

        {err && (
          <div className="alert alert-danger" role="alert">
            {err}
          </div>
        )}

        <div
          onDragOver={(e) => {
            e.preventDefault();
            setHover(true);
          }}
          onDragLeave={() => setHover(false)}
          onDrop={onDrop}
          className={`p-2 mb-2 rounded border ${hover ? 'border-primary bg-light' : 'border-secondary-subtle'}`}
          style={{ borderStyle: 'dashed', borderWidth: 2 }}
        >
          <label htmlFor="cr-description" className="form-label">
            What should change? <span aria-hidden="true">*</span>
          </label>
          <textarea
            id="cr-description"
            className="form-control"
            rows={6}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            onPaste={onPaste}
            maxLength={8000}
            required
            aria-required="true"
            disabled={submitting}
            placeholder="Tell Copilot what to change. Mention the page and what you want it to look like. You can paste a screenshot here too."
            aria-describedby="cr-desc-help"
          />
          <div id="cr-desc-help" className="form-text">
            Tip: paste a screenshot directly into the box, or drag/drop an image anywhere in this card.
          </div>
        </div>

        {image && <ImageAttachment image={image} onRemove={() => setImage(null)} />}

        <div className="d-flex justify-content-between align-items-center">
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => fileInput.current?.click()}
            disabled={submitting}
          >
            Attach screenshot
          </button>
          <input
            ref={fileInput}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif"
            className="d-none"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void attachFile(f);
              e.target.value = '';
            }}
          />
          <button
            type="submit"
            className="btn btn-primary"
            disabled={submitting || !description.trim()}
          >
            {submitting ? 'Submitting…' : 'Submit request'}
          </button>
        </div>
      </div>
    </form>
  );
}

function DetailPanel({
  detail,
  loading,
  onRefresh,
  onListRefresh,
  onClose
}: {
  detail: ChangeDetail | null;
  loading: boolean;
  onRefresh: () => void;
  onListRefresh: () => void;
  onClose: () => void;
}) {
  const [reply, setReply] = useState('');
  const [posting, setPosting] = useState(false);
  const [deploying, setDeploying] = useState(false);
  const [err, setErr] = useState('');
  const [info, setInfo] = useState('');

  if (loading && !detail) {
    return (
      <div className="card">
        <div className="card-body"><LoadingBlock label="Loading change requests…" /></div>
      </div>
    );
  }
  if (!detail) return null;

  const post = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reply.trim()) return;
    setPosting(true);
    setErr('');
    try {
      await api.post(`/api/mgmt/change-requests/${detail.number}/comments`, {
        body: reply.trim(),
        mentionCopilot: true,
        postToPr: detail.status === 'review' || detail.status === 'working'
      });
      setReply('');
      onRefresh();
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : 'Could not post message.');
    } finally {
      setPosting(false);
    }
  };

  const deploy = async () => {
    if (!confirm('Deploy this change to the live site? Once merged, it will go live in a few minutes.')) return;
    setDeploying(true);
    setErr('');
    setInfo('');
    try {
      const res = await api.post<{ merged: boolean; message: string }>(
        `/api/mgmt/change-requests/${detail.number}/merge`
      );
      setInfo(res.merged ? 'Merged! The site will update in a few minutes.' : res.message);
      onRefresh();
      onListRefresh();
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : 'Deploy failed.');
    } finally {
      setDeploying(false);
    }
  };

  const canDeploy = detail.status === 'review';

  return (
    <div className="card">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
          <div>
            <h2 className="h5 mb-1">
              #{detail.number} {detail.title}
            </h2>
            <div className="small text-muted">
              Opened {timeAgo(detail.createdAt)}
              {detail.requestedBy ? ` by ${detail.requestedBy}` : ''}
            </div>
          </div>
          <div className="d-flex flex-column align-items-end gap-1">
            {statusBadge(detail.status)}
            <button type="button" className="btn btn-sm btn-link" onClick={onClose}>
              Close
            </button>
          </div>
        </div>

        {info && (
          <div className="alert alert-success" role="status">
            {info}
          </div>
        )}
        {err && (
          <div className="alert alert-danger" role="alert">
            {err}
          </div>
        )}

        <div className="d-flex gap-2 flex-wrap mb-3">
          {canDeploy && (
            <button
              type="button"
              className="btn btn-success"
              onClick={deploy}
              disabled={deploying}
            >
              {deploying ? 'Deploying…' : 'Deploy to site'}
            </button>
          )}
          <a className="btn btn-sm btn-outline-secondary" href={detail.url} target="_blank" rel="noopener noreferrer">
            View on GitHub
          </a>
          {detail.prUrl && (
            <a className="btn btn-sm btn-outline-primary" href={detail.prUrl} target="_blank" rel="noopener noreferrer">
              View pull request #{detail.prNumber}
            </a>
          )}
        </div>

        <div className="border rounded p-3 mb-3 bg-light">
          <div className="small text-muted mb-1">Original request</div>
          <pre className="mb-0" style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>
            {detail.body}
          </pre>
        </div>

        <h3 className="h6">Conversation</h3>
        {detail.comments.length === 0 ? (
          <p className="text-muted small">No replies yet. Copilot will respond once it picks this up.</p>
        ) : (
          <ul className="list-unstyled mb-3">
            {detail.comments.map((c) => (
              <li key={`${c.source}-${c.id}`} className="mb-3">
                <div className="d-flex justify-content-between small text-muted mb-1">
                  <span>
                    <strong>{c.author}</strong>
                    {c.authorType === 'bot' ? ' (bot)' : ''}
                    {c.source === 'pr' ? ' · on PR' : ''}
                  </span>
                  <span>{timeAgo(c.createdAt)}</span>
                </div>
                <div className="border rounded p-2 bg-white">
                  <pre className="mb-0" style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>
                    {c.body}
                  </pre>
                </div>
              </li>
            ))}
          </ul>
        )}

        <form onSubmit={post}>
          <label htmlFor="cr-reply" className="form-label">
            Send a follow-up message
          </label>
          <textarea
            id="cr-reply"
            className="form-control mb-2"
            rows={3}
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            maxLength={8000}
            disabled={posting}
            placeholder="e.g. 'Make the heading bigger' or 'Use a different shade of blue'"
            aria-describedby="cr-reply-help"
          />
          <div id="cr-reply-help" className="form-text mb-2">
            Your message is sent to Copilot, which will update the pull request.
          </div>
          <button type="submit" className="btn btn-primary btn-sm" disabled={posting || !reply.trim()}>
            {posting ? 'Sending…' : 'Send'}
          </button>
        </form>
      </div>
    </div>
  );
}
